import { useState, useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { useDealsStore } from '@/stores/useDealsStore'
import { useAuthStore } from '@/stores/authStore'
import { getStageLabel } from '@/components/Deals/deals-constants'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { handleDealStageMoveError } from '@/utils/handleDealStageMoveError'
import { REUNIAO_REQUIRED_FIELD } from '@/utils/dealMeetingSchedule'
import { resolveStageTransitionIntent } from '@/services/dealStageTransitionFlow'
import { patchDealStage } from '@/services/patchDealStage'
import { extractGoalsList, mapApiGoalsToTargets } from '@/utils/goalTargets'
import { canReadParceiros } from '@/utils/permissions'
import type { Deal, DealStage } from '@/types'
import type { ContractDataForm, ContractDataModalMode } from '@/components/Deals/DealDetailPanel/contractDataUtils'
import {
  getMissingContractDataFields,
  getMissingHandoverCompletionFields,
  isContractDataComplete,
  isHandoverCompletionComplete,
} from '@/components/Deals/DealDetailPanel/contractDataUtils'
import { persistContractDataForDeal } from '@/components/Deals/DealDetailPanel/contractDataFlow'
import { useDealsListFetch } from './useDealsListFetch'

export type PendingTransition = { dealId: string; targetStage: DealStage } | null

export type { DealsListMeta } from './useDealsListFetch'

export function useDealsPageHandlers() {
  const { user } = useAuthStore()
  const {
    deals,
    updateDealById,
    setPlans,
    setServices,
    setPartners,
    setGoalTargets,
    setSelectedDeal,
    setDealPanelHighlight,
    setDealContractModalOpen,
  } = useDealsStore()
  const {
    dealsMeta,
    hasMoreDeals,
    loadingMore,
    listError,
    fetchDeals,
    loadMoreDeals,
  } = useDealsListFetch()

  const [handoffDeal, setHandoffDeal] = useState<Deal | null>(null)
  const [pendingTransition, setPendingTransition] = useState<PendingTransition>(null)
  const [showLossModal, setShowLossModal] = useState(false)
  const [showFutureModal, setShowFutureModal] = useState(false)
  const [showWinModal, setShowWinModal] = useState(false)
  const [showBantModal, setShowBantModal] = useState(false)
  const [showStageChoiceModal, setShowStageChoiceModal] = useState(false)
  const [showContractDataModal, setShowContractDataModal] = useState(false)
  const [contractDataDeal, setContractDataDeal] = useState<Deal | null>(null)
  const [contractDataModalMode, setContractDataModalMode] =
    useState<ContractDataModalMode>('gerar_contrato')

  useEffect(() => {
    setDealContractModalOpen(showContractDataModal)
    return () => setDealContractModalOpen(false)
  }, [showContractDataModal, setDealContractModalOpen])

  const fetchMetadata = useCallback(async () => {
    const empty = { data: { data: [] } }
    const partnersRequest = canReadParceiros(user?.role)
      ? api.get('/parceiros')
      : Promise.resolve(empty)

    const [plansRes, servicesRes, partnersRes, goalsRes] = await Promise.allSettled([
      api.get('/plans'),
      api.get('/services'),
      partnersRequest,
      api.get('/goals').catch(() => ({ data: [] })),
    ])

    if (plansRes.status === 'fulfilled') {
      setPlans(plansRes.value.data.data ?? plansRes.value.data ?? [])
    }
    if (servicesRes.status === 'fulfilled') {
      setServices(servicesRes.value.data.data ?? servicesRes.value.data ?? [])
    }
    if (partnersRes.status === 'fulfilled') {
      setPartners(partnersRes.value.data.data ?? partnersRes.value.data ?? [])
    }
    if (goalsRes.status === 'fulfilled') {
      const goalsList = extractGoalsList(goalsRes.value.data)
      if (goalsList.length > 0) setGoalTargets(mapApiGoalsToTargets(goalsList))
    }
  }, [setPlans, setServices, setPartners, setGoalTargets, user?.role])

  const handleMoveStage = useCallback(
    async (dealId: string, newStage: DealStage) => {
      const deal = deals.find((d) => d.id === dealId)
      if (!deal) return

      const intent = resolveStageTransitionIntent(deal, newStage, user?.role)
      switch (intent.type) {
        case 'noop':
          return
        case 'blocked': {
          if (intent.code === 'REUNIAO_REQUIRED') {
            setSelectedDeal(deal)
            setDealPanelHighlight({
              dealId: deal.id,
              fields: intent.highlightFields ?? [REUNIAO_REQUIRED_FIELD],
            })
          }
          toast.error(intent.reason)
          return
        }
        case 'open_stage_choice':
          setPendingTransition({ dealId: intent.dealId, targetStage: intent.targetStage })
          setShowStageChoiceModal(true)
          return
        case 'open_loss_modal':
          setPendingTransition({ dealId: intent.dealId, targetStage: intent.targetStage })
          setShowLossModal(true)
          return
        case 'open_future_modal':
          setPendingTransition({ dealId: intent.dealId, targetStage: intent.targetStage })
          setShowFutureModal(true)
          return
        case 'open_win_modal':
          setPendingTransition({ dealId: intent.dealId, targetStage: intent.targetStage })
          if (deal.etapa === 'handover') {
            setContractDataDeal(deal)
            setContractDataModalMode('handover_complete')
            setShowContractDataModal(true)
          } else {
            setShowWinModal(true)
          }
          return
        case 'open_bant_modal':
          setPendingTransition({ dealId: intent.dealId, targetStage: intent.targetStage })
          setShowBantModal(true)
          return
        case 'patch': {
          if (intent.targetStage === 'gerar_contrato') {
            setSelectedDeal(deal)
            setPendingTransition({ dealId: intent.dealId, targetStage: 'gerar_contrato' })
            setContractDataDeal(deal)
            setContractDataModalMode('gerar_contrato')
            setShowContractDataModal(true)
            return
          }

          const previousEtapa = deal.etapa
          updateDealById(dealId, { etapa: intent.targetStage })
          try {
            const updated = await patchDealStage(
              intent.dealId,
              intent.targetStage,
              intent.qualification,
            )
            updateDealById(dealId, updated)
            toast.success(`Deal movido para ${getStageLabel(intent.targetStage)}`)
          } catch (err: unknown) {
            updateDealById(dealId, { etapa: previousEtapa })
            fetchDeals(true)
            handleDealStageMoveError(err, deal, {
              setSelectedDeal,
              setDealPanelHighlight,
              toastError: toast.error,
            })
          }
          return
        }
      }
    },
    [deals, updateDealById, fetchDeals, setSelectedDeal, setDealPanelHighlight],
  )

  const handleBantSave = useCallback(
    async (updates: { budgetConfirmado: string; decisorPresente: string; doresIdentificadas: string }) => {
      if (!pendingTransition) return
      const { dealId, targetStage } = pendingTransition
      try {
        const updated = await patchDealStage(dealId, targetStage, updates)
        updateDealById(dealId, updated)
        toast.success(`Qualificação salva — deal em "${getStageLabel(targetStage)}"`)
        setPendingTransition(null)
        setShowBantModal(false)
      } catch (err: unknown) {
        fetchDeals(true)
        toast.error(getFriendlyError(err, 'Erro ao salvar qualificação e mover deal'))
        throw new Error('bant_stage_failed')
      }
    },
    [pendingTransition, updateDealById, fetchDeals],
  )

  const handleLossSave = useCallback(
    async (updates: { motivoPerda: string; submotivoPerda?: string; comentarioPerda?: string }) => {
      if (!pendingTransition) return
      const { dealId } = pendingTransition

      updateDealById(dealId, { etapa: 'perdido' as DealStage, ...updates })

      try {
        const updated = await patchDealStage(dealId, 'perdido' as DealStage, updates)
        updateDealById(dealId, updated)
        toast.success('Deal marcado como perdido')
        setPendingTransition(null)
        setShowLossModal(false)
      } catch (err: unknown) {
        fetchDeals(true)
        toast.error(getFriendlyError(err, 'Erro ao salvar motivo da perda'))
        throw new Error('loss_stage_failed')
      }
    },
    [pendingTransition, updateDealById, fetchDeals],
  )

  const handleFutureSave = useCallback(
    async (updates: { dataFechamentoPrevisto?: string; observacoes?: string }) => {
      if (!pendingTransition) return
      const { dealId } = pendingTransition

      updateDealById(dealId, {
        etapa: 'fechamento_futuro' as DealStage,
        dataFechamentoPrevisto: updates.dataFechamentoPrevisto,
      })

      try {
        const updated = await patchDealStage(dealId, 'fechamento_futuro' as DealStage, updates)
        updateDealById(dealId, updated)
        toast.success('Deal agendado para o futuro')
        setPendingTransition(null)
        setShowFutureModal(false)
      } catch (err: unknown) {
        fetchDeals(true)
        toast.error(getFriendlyError(err, 'Erro ao salvar fechamento futuro'))
        throw new Error('future_stage_failed')
      }
    },
    [pendingTransition, updateDealById, fetchDeals],
  )

  const handleWinSave = useCallback(
    async (updates: { dataFechamento: string; observacoes: string }) => {
      if (!pendingTransition) return
      const { dealId } = pendingTransition

      updateDealById(dealId, {
        etapa: 'ganho' as DealStage,
        dataFechamento: updates.dataFechamento,
        observacoes: updates.observacoes,
      })

      try {
        const updated = await patchDealStage(dealId, 'ganho' as DealStage, updates)
        updateDealById(dealId, updated)
        toast.success('Deal marcado como ganho')
        setPendingTransition(null)
        setShowWinModal(false)
      } catch (err: unknown) {
        fetchDeals(true)
        toast.error(getFriendlyError(err, 'Erro ao marcar como ganho'))
        throw new Error('win_stage_failed')
      }
    },
    [pendingTransition, updateDealById, fetchDeals],
  )

  const handleStageChoice = useCallback(
    (stage: DealStage) => {
      if (!pendingTransition) return
      const { dealId } = pendingTransition
      const deal = deals.find((d) => d.id === dealId)
      setShowStageChoiceModal(false)

      if (stage === 'perdido') {
        setPendingTransition({ dealId, targetStage: 'perdido' })
        setShowLossModal(true)
        return
      }

      if (stage === 'gerar_contrato' && deal) {
        setPendingTransition({ dealId, targetStage: 'gerar_contrato' })
        setContractDataDeal(deal)
        setContractDataModalMode('gerar_contrato')
        setShowContractDataModal(true)
        return
      }

      setPendingTransition(null)
      void handleMoveStage(dealId, stage)
    },
    [pendingTransition, deals, handleMoveStage],
  )

  const handleContractDataConfirm = useCallback(
    async (form: ContractDataForm) => {
      if (!pendingTransition) return
      const { dealId, targetStage } = pendingTransition
      const deal = deals.find((d) => d.id === dealId)
      if (!deal) return

      if (contractDataModalMode === 'handover_complete' && targetStage === 'ganho') {
        const missing = getMissingHandoverCompletionFields(form)
        if (!isHandoverCompletionComplete(form)) {
          toast.error(`Preencha todos os dados: ${missing.join(', ')}`)
          return
        }

        try {
          const updated = await patchDealStage(dealId, 'ganho', {
            dataFechamento: new Date(form.dataFechamentoHandover).toISOString(),
            observacoes: form.observacoesHandover.trim(),
          })
          updateDealById(dealId, updated)
          toast.success('Handover concluído — deal registrado como ganho')
          setShowContractDataModal(false)
          setContractDataDeal(null)
          setContractDataModalMode('gerar_contrato')
          setPendingTransition(null)
        } catch (err: unknown) {
          fetchDeals(true)
          toast.error(getFriendlyError(err, 'Erro ao concluir handover'))
          throw err
        }
        return
      }

      if (targetStage !== 'gerar_contrato') return

      const missing = getMissingContractDataFields(form)
      if (!isContractDataComplete(form)) {
        toast.error(`Preencha todos os dados: ${missing.join(', ')}`)
        return
      }

      try {
        const { deal: updatedDealFields } = await persistContractDataForDeal(deal, form)
        updateDealById(dealId, updatedDealFields)

        updateDealById(dealId, { ...updatedDealFields, etapa: 'gerar_contrato' })
        const updated = await patchDealStage(dealId, 'gerar_contrato')
        updateDealById(dealId, updated)
        toast.success('Dados enviados. Aguardando retorno do financeiro para continuar.')
        setShowContractDataModal(false)
        setContractDataDeal(null)
        setContractDataModalMode('gerar_contrato')
        setPendingTransition(null)
        setSelectedDeal(null)
      } catch (err: unknown) {
        updateDealById(dealId, { etapa: deal.etapa })
        fetchDeals(true)
        toast.error(getFriendlyError(err, 'Erro ao salvar dados do contrato'))
        throw err
      }
    },
    [pendingTransition, contractDataModalMode, deals, updateDealById, fetchDeals, setSelectedDeal],
  )

  const handleCancelModal = useCallback(() => {
    setPendingTransition(null)
    setShowLossModal(false)
    setShowFutureModal(false)
    setShowWinModal(false)
    setShowBantModal(false)
    setShowStageChoiceModal(false)
    setShowContractDataModal(false)
    setContractDataDeal(null)
    setContractDataModalMode('gerar_contrato')
    fetchDeals(true)
  }, [fetchDeals])

  const handleHandoffAccept = useCallback(async () => {
    if (!handoffDeal) return
    try {
      await api.post(`/deals/${handoffDeal.id}/accept`)
      toast.success('Handoff aceito! Deal atribuído.')
      fetchDeals(true)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao aceitar handoff'))
    }
    setHandoffDeal(null)
  }, [handoffDeal, fetchDeals])

  const handleHandoffReject = useCallback(
    async (reason: string) => {
      if (!handoffDeal) return
      try {
        await api.post(`/deals/${handoffDeal.id}/reject`, { reason })
        toast.info('Handoff rejeitado. Deal devolvido ao BDR.')
        fetchDeals(true)
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao rejeitar handoff'))
      }
      setHandoffDeal(null)
    },
    [handoffDeal, fetchDeals],
  )

  return {
    handoffDeal,
    setHandoffDeal,
    pendingTransition,
    dealsMeta,
    hasMoreDeals,
    loadingMore,
    loadMoreDeals,
    showLossModal,
    showFutureModal,
    showWinModal,
    showBantModal,
    showStageChoiceModal,
    showContractDataModal,
    contractDataDeal,
    contractDataModalMode,
    listError,
    fetchDeals,
    fetchMetadata,
    handleMoveStage,
    handleBantSave,
    handleLossSave,
    handleFutureSave,
    handleWinSave,
    handleStageChoice,
    handleContractDataConfirm,
    handleCancelModal,
    handleHandoffAccept,
    handleHandoffReject,
  }
}
